import { AccountingType, LoopModule, PLATFORM } from '@LoopKitchen/frontend-client'
import { API_URLS } from 'src/services/openApiV2/utils/api.config'
import packageJson from '../../../package.json'

// Log the OATS mode for debugging
if (import.meta.env.VITE_OATS_MODE === 'true') {
  console.log('🌾 OATS Mode Detected - Using local backend:', API_URLS.BASE_API_URL)
}

// Re-export API URLs from centralized config for backward compatibility
export const BASE_URL = 'app.tryloop.ai'
export const BASE_AUTH_URL = 'auth.tryloop.ai' // Note: hostname only, no protocol (used by Firebase authDomain)
export const BASE_FRONTEND_URL = API_URLS.BASE_FRONTEND_URL
export const BASE_API_LOCAL_URL = API_URLS.BASE_API_LOCAL_URL
export const BASE_API_URL = API_URLS.BASE_API_URL
export const BASE_API_HOST = API_URLS.BASE_API_HOST
export const BASE_FIREBASE_FUNCTIONS_URL = API_URLS.BASE_FIREBASE_FUNCTIONS_URL
export const BASE_DOCS_URL = API_URLS.BASE_DOCS_URL
export const BASE_AGENT_URL = API_URLS.BASE_AGENT_URL
export const TRUROI_AGENT_BASE_URL = API_URLS.TRUROI_AGENT_BASE_URL
export const LOOP_SHEETS_AGENT_URL = API_URLS.LOOP_SHEETS_AGENT_URL
export const APP_SERVICE_BASE_URL = API_URLS.APP_SERVICE_BASE_URL

export const APP_NAME = packageJson.name
export const APP_VERSION = packageJson.version
export const AUTH_ENCRYPTION_KEY = 'RnJhbmtseVNwZWFraW5nVGhpc0lzUmFuZG9t'

export const LOOPKITCHENXYZ_DOMAIN = 'loopkitchen.xyz'
export const LOOPAIXYZ_DOMAIN = 'loopai.xyz'
export const TRYLOOPAI_DOMAIN = 'tryloop.ai'

export const LOOP_SUPPORT_EMAIL = 'support@tryloop.ai'
export const LOOP_SUPPORT_MAIL_EMAIL = 'support@mail.tryloop.ai'
export const R365_SUPPORT_EMAIL = 'support@restaurant365.com'

export const SLACK_OAUTH_URL =
  'https://slack.com/oauth/v2/authorize?client_id=3674891125905.4553087581846&scope=incoming-webhook,chat:write,chat:write.customize,chat:write.public,links.embed:write&user_scope=chat:write,users:read,users:write'

export const filterNames = {
  b_name: 'Location',
  vb_name: 'Brand',
  vb_platform: 'Marketplace',
  vb_address: 'Address',
  am_name: 'Account Manager',
  slug: 'Digital Stores'
}

export const filterIcons = {
  b_name: 'museum',
  vb_name: 'workspaces',
  vb_platform: 'store',
  slug: 'store'
}

export enum AccessLevelEnum {
  BUSINESS_ADMIN = 'business-admin',
  BUSINESS_MANAGER = 'business-manager',
  DEMO = 'demo',
  FINANCE_TEAM = 'finance-team',
  INTERNAL_OPERATOR = 'internal-operator',
  OWNER_VIEW = 'owner-view',
  STORE_MANAGER = 'store-manager',
  OBSERVER = 'observer'
}

export const ModuleNameMapping = {
  [LoopModule.GUARD]: 'Guard',
  [LoopModule.TRUROI]: 'TruROI',
  [LoopModule.RECOVER]: 'Recover',
  [LoopModule.REENG]: 'Re-Engage',
  [LoopModule.BASE]: 'Base',
  [LoopModule.BALANCE]: 'Balance',
  QUANT: 'Quant'
}

export const Modules = {
  StoreAvailability: ModuleNameMapping[LoopModule.GUARD],
  Chargebacks: 'Customer Refunds',
  Recover: ModuleNameMapping[LoopModule.RECOVER],
  TotalChargebacks: 'Customer Refunds',
  EnabledChargebacks: 'Loop Enabled Customer Refunds',
  Balance: ModuleNameMapping[LoopModule.BALANCE],
  WonDisputes: 'Won Disputes',
  LostSales: 'Unfulfilled Sales',
  ErrorCharges: 'Error Charges',
  Marketing: ModuleNameMapping[LoopModule.TRUROI],
  ReviewsAndRatings: ModuleNameMapping[LoopModule.REENG],
  UnfulfilledRefunds: 'Unfulfilled Refunds'
}

export const AccountingTypeNames = {
  [AccountingType.CASH]: 'Payouts-Based',
  [AccountingType.ACCRUAL]: 'Transactions-Based'
}

export const accessLevelConfig: Record<AccessLevelEnum, { label: string }> = {
  [AccessLevelEnum.BUSINESS_ADMIN]: {
    label: 'Business Admin'
  },
  [AccessLevelEnum.BUSINESS_MANAGER]: {
    label: 'Business Manager'
  },
  [AccessLevelEnum.DEMO]: {
    label: 'Demo'
  },
  [AccessLevelEnum.FINANCE_TEAM]: {
    label: 'Accounting'
  },
  [AccessLevelEnum.INTERNAL_OPERATOR]: {
    label: 'Franchise Admin'
  },
  [AccessLevelEnum.OWNER_VIEW]: {
    label: 'Owner View'
  },
  [AccessLevelEnum.STORE_MANAGER]: {
    label: 'Store Manager'
  },
  [AccessLevelEnum.OBSERVER]: {
    label: 'Observer'
  }
}

export const platformNames = {
  [PLATFORM.DOORDASH]: 'DoorDash',
  [PLATFORM.GRUBHUB]: 'Grubhub',
  [PLATFORM.UBER_EATS]: 'Uber Eats',
  [PLATFORM.EZ_CATER]: 'ezCater',
  [PLATFORM.OLO]: 'Olo',
  [PLATFORM.LUNCHBOX]: 'Lunchbox',
  [PLATFORM.SKIP_THE_DISHES]: 'Skip the Dishes'
}

export const StateNameToAbbreviation = {
  Alabama: 'AL',
  Alaska: 'AK',
  Arizona: 'AZ',
  Arkansas: 'AR',
  California: 'CA',
  Colorado: 'CO',
  Connecticut: 'CT',
  Delaware: 'DE',
  Florida: 'FL',
  Georgia: 'GA',
  Hawaii: 'HI',
  Idaho: 'ID',
  Illinois: 'IL',
  Indiana: 'IN',
  Iowa: 'IA',
  Kansas: 'KS',
  Kentucky: 'KY',
  Louisiana: 'LA',
  Maine: 'ME',
  Maryland: 'MD',
  Massachusetts: 'MA',
  Michigan: 'MI',
  Minnesota: 'MN',
  Mississippi: 'MS',
  Missouri: 'MO',
  Montana: 'MT',
  Nebraska: 'NE',
  Nevada: 'NV',
  'New Hampshire': 'NH',
  'New Jersey': 'NJ',
  'New Mexico': 'NM',
  'New York': 'NY',
  'North Carolina': 'NC',
  'North Dakota': 'ND',
  Ohio: 'OH',
  Oklahoma: 'OK',
  Oregon: 'OR',
  Pennsylvania: 'PA',
  'Rhode Island': 'RI',
  'South Carolina': 'SC',
  'South Dakota': 'SD',
  Tennessee: 'TN',
  Texas: 'TX',
  Utah: 'UT',
  Vermont: 'VT',
  Virginia: 'VA',
  Washington: 'WA',
  'West Virginia': 'WV',
  Wisconsin: 'WI',
  Wyoming: 'WY',
  'District of Columbia': 'DC',
  'American Samoa': 'AS',
  Guam: 'GU',
  'Northern Mariana Islands': 'MP',
  'Puerto Rico': 'PR',
  'United States Minor Outlying Islands': 'UM',
  'Virgin Islands, U.S.': 'VI'
}

export const StateAbbreviationToName = Object.fromEntries(Object.entries(StateNameToAbbreviation).map(([key, value]) => [value, key]))

export const OrgsWithWholeNumberCogs = ['capriottis']

export const swrRefreshIntervalMilliseconds = 45 * 60 * 1000 // 45 minutes
export const automaticallyUpdateFiltersDuration = 10 * 60 * 1000 // 10 minutes

export const blockedOrgIds = ['sandbox', 'worldwide']

// export const blockedApiEndpointsForSandbox = [
//   'bulkUploadBnamesV2BnamesBulkUploadV2Post',
//   'createBnameBnamesPost',
//   'updateDigitalStoresStoreMappingStoresUpdatePost',
//   'updateBnameBnamesBnameIdPut',
//   'deleteBnameBnamesBnameIdDelete',
//   'bulkDeleteDigitalStoresFromListDigitalStoresBulkDeleteFromListPost',
//   'bulkUploadDigitalStoresV2DigitalStoresBulkUploadV2Post',
//   'restoreDeletedStoreStoreMappingDeletedRestorePost',
//   'runCustomerOnboardingActionsOnboardingCustomerPost',
//   'addLoginUsernameStoreMappingUsernameAddPost',
//   'getStatusReasonsBulkGetBackendStatusReasonBulkPost',
//   'refreshStoreAvailabilityStoreAvailabilityRefreshSlugPost',
//   'setStoresStatusSetStoreStatusPost',
//   'setStoresStatusBulkSetStoreOnlineBulkPost',
//   'getStatusReasonsGetBackendStatusReasonGet',
//   'stopCampaignsStopCampaignPost',
//   'startCampaignStartCampaignPost',
//   'inviteOrgUserOrgInviteUserPost',
//   'triggerEmailDigestNotificationEmailDigestPost',
//   'deleteOrgUserOrgUserUserEmailDelete',
//   'getOrgUsersOrgUsersGet',
//   'inviteOrgUserOrgInviteUserPost',
//   'updateBnamesToUserAdminUpdateBnamesPut',
//   'createCompanyCodatCreateCompanyPost',
//   'setAccessTokenPlaidSetAccessTokenPost',
//   'generateReviewSummaryReviewsSummaryGeneratePost',
//   'setUserNotificationConfigsNotificationConfigSetPost',
//   'triggerPushJournalEntriesV2AccountingJournalEntriesTriggerPushV2Post',
//   'updateCompanyCodatUpdateConnectionsPost',
//   'createAccountingSageCompanySageCreateCompanyPost',
//   'createAccountingNetsuiteCompanyNetsuiteCreateCompanyPost',
//   'createAccountingR365CompanyR365CreateCompanyPost',
//   'createAccountingFreetextCompanyFreetextCreateCompanyPost',
//   'authorizeLinkAuthorizeGet',
//   'setPlaidMappingsPlaidMappingsSetPost',
//   'disconnectAccountPlaidDeleteAccountDelete',
//   'setBnameMappingsAccountingBnameMappingsSetPost',
//   'setAccountingCadenceConfigAccountingConfigurationSetPost',
//   'setGlMappingsAccountingGlMappingsSetPost',
//   'updateFreetextCompanyGlCodesFreetextCompanyGlCodesPost',
//   'deleteAccountingCadenceConfigAccountingConfigurationDeleteDelete',
//   'updateR365CompanyClassesR365CompanyClassesPost',
//   'updateFreetextCompanyClassesFreetextCompanyClassesPost',
//   'reviewReplyReviewReplyPost',
//   'getItemMetricsCompetitionItemMetricsPost',
//   'getStoresWithItemCompetitionItemStoresPost',
//   'updateDigitalStoreDigitalStoresDigitalStoreIdPut',
//   'deleteDigitalStoresDigitalStoresDigitalStoreIdDelete',
//   'mapUsersToSubchainsSubchainMapUserPost', // assigns users to subchain
//   'getAllUserSubChainsSubchainOrgUsersGet', // get all users for org, with assigned subchains
//   'createSubchainSubchainSetPost', // create subchain
//   'deleteSubchainSubchainDeleteDelete', // delete subchain
//   'createOrUpdateChargebackConfigV2ConfigChargebackCreateOrUpdateV2Post'
// ]

// Blocked API endpoint patterns for sandbox users (method:url format)
export const blockedApiEndpointPatterns = [
  'POST:/bnames/bulk/upload/v2', // Bnames management
  'POST:/bnames',
  'PUT:/bnames/{bname_id}',
  'DELETE:/bnames/{bname_id}',
  'POST:/stores/update', // Digital stores management
  'POST:/digital-stores/bulk-delete-from-list',
  'POST:/digital-stores/bulk/upload/v2',
  'POST:/store-mapping/deleted/restore',
  'PUT:/digital_stores/{digital_store_id}',
  'DELETE:/digital_stores/{digital_store_id}',
  'POST:/onboarding/customer', // Store operations
  'POST:/store-mapping/username/add',
  'POST:/get-backend-status-reason/bulk',
  'POST:/store-availability/refresh/{slug}',
  'POST:/set-store-status',
  'POST:/set-store-online/bulk',
  'GET:/get-backend-status-reason',
  'POST:/stop-campaign', // Campaign management
  'POST:/start_campaign',
  'POST:/org/invite/user', // Organization and user management
  'DELETE:/org/user/{user_email}',
  'GET:/org/users',
  'PUT:/admin/update/bnames',
  'POST:/notification/email_digest', // Notifications
  'POST:/notification/config/set',
  'POST:/codat/create/company', // Accounting integrations
  'POST:/codat/update/connections',
  'POST:/sage/create/company',
  'POST:/netsuite/create/company',
  'POST:/r365/create/company',
  'POST:/freetext/create/company',
  'POST:/accounting/journal_entries/trigger/push/v2',
  'POST:/accounting/bname_mappings/set',
  'POST:/accounting/configuration/set',
  'POST:/accounting/gl_mappings/set',
  'POST:/freetext/company/gl_codes',
  'DELETE:/accounting/configuration/delete',
  'POST:/r365/company/classes',
  'POST:/freetext/company/classes',
  'POST:/plaid/set_access_token', // Plaid integration
  'GET:/authorize',
  'POST:/plaid/mappings/set',
  'DELETE:/plaid/delete/account',
  'POST:/reviews/summary/generate', // Reviews
  'POST:/review/reply',
  'POST:/competition/item/metrics', // Competition (Note: These endpoints might need verification)
  'POST:/competition/item/stores',
  'POST:/subchain/map/user', // Subchain management
  'GET:/subchain/org/users',
  'POST:/subchain/set',
  'DELETE:/subchain/delete',
  'POST:/config/chargeback/create_or_update/v2' // Chargeback configuration
]
